import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loading, Error, useNotify } from "react-admin";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";

const STATUS_OPTIONS = [
  "In Progress",
  "Waiting for Donors Confirmation",
  "Open",
  "Closed",
];

const TicketShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const messagesEndRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState("");

  const [ticketData, setTicketData] = useState({
    status: "Open",
  });

  const [selectedStatus, setSelectedStatus] = useState(ticketData.status);

  const fetchMessages = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}admin/messages/${id}`,
        { headers: { Authorization: token } }
      );

      if (!res.ok) throw new Error("Failed to fetch messages");

      const data = await res.json();

      setMessages(data.data || []);
      setTicketData({
        name: data.name || "",
        email: data.email || "",
        status: data.status || "Open",
      });
      setSelectedStatus(formatStatus(data.status || "Open"));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchMessages();
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  }, [messages]);

  const sendMessage = async () => {
    if (!newMessage.trim())
      return notify("Message cannot be empty", { type: "warning" });

    const token = localStorage.getItem("token");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}admin/ticket/reply`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({
            ticketId: id,
            senderType: "admin",
            message: newMessage,
          }),
        }
      );

      if (!res.ok) throw new Error("Failed to send message");

      setNewMessage("");
      fetchMessages();
    } catch (err) {
      notify("Error sending message", { type: "error" });
    }
  };

  // Update status via your API
  const updateMessageStatus = async (newStatus) => {
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}admin/messages/${id}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (!res.ok) throw new Error("Failed to update status");

      notify(`Status updated to "${newStatus}"`, { type: "success" });
      setTicketData((prev) => ({ ...prev, status: newStatus }));
      setSelectedStatus(formatStatus(newStatus));
    } catch (err) {
      notify("Error updating status", { type: "error" });
    }
  };

  const groupedMessages = messages.reduce((acc, msg) => {
    const dateKey = new Date(msg.createdAt).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    acc[dateKey] = acc[dateKey] || [];
    acc[dateKey].push(msg);
    return acc;
  }, {});

  if (loading) return <Loading />;
  if (error) return <Error error={error} />;

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden", // Prevent page scroll outside chat window
      }}
    >
      {/* HEADER */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          padding: "12px 24px",
          background: "#000",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 12,
          justifyContent: "space-between",
          flexShrink: 0,
          height: 60,
        }}
      >
        <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: "#fff" }}>
          <ArrowBackIcon />
        </IconButton>

        {/* Ticket number */}
        <h3 style={{ margin: 0, flexGrow: 1 }}>Ticket #{id}</h3>

        {/* Dropdown to update status */}
        <select
          value={selectedStatus}
          onChange={(e) => {
            setSelectedStatus(formatStatus(e.target.value));
            updateMessageStatus(e.target.value);
          }}
          style={{
            padding: "6px 14px",
            borderRadius: 6,
            fontSize: 14,
            cursor: "pointer",
            minWidth: 220,
          }}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      {/* MESSAGES */}
      <div
        style={{
          flexGrow: 1,
          overflowY: "auto",
          padding: 24,
          minHeight: 0,
        }}
      >
        {Object.entries(groupedMessages).map(([date, msgs]) => (
          <div key={date}>
            <div style={{ textAlign: "center", fontSize: 12, color: "#999" }}>
              {date}
            </div>

            {msgs.map((msg) => {
              const isAdmin = msg.senderType === "admin";
              return (
                <div
                  key={msg._id}
                  style={{
                    display: "flex",
                    justifyContent: isAdmin ? "flex-end" : "flex-start",
                    marginTop: 10,
                  }}
                >
                  <div
                    style={{
                      padding: "10px 14px",
                      borderRadius: 16,
                      background: isAdmin ? "#1976d2" : "#eee",
                      color: isAdmin ? "#fff" : "#333",
                      maxWidth: "65%",
                    }}
                  >
                    {msg.message}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* INPUT */}
      <div
        style={{
          padding: 16,
          display: "flex",
          gap: 10,
          borderTop: "1px solid #ddd",
          flexShrink: 0,
          height: 60,
        }}
      >
        <input
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          placeholder="Type message..."
          style={{
            flex: 1,
            padding: 12,
            borderRadius: 20,
            border: "1px solid #ccc",
          }}
        />

        <button
          onClick={sendMessage}
          style={{
            padding: "10px 24px",
            borderRadius: 20,
            border: "none",
            background: "#1976d2",
            color: "#fff",
            cursor: "pointer",
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default TicketShow;
const formatStatus = (status) => {
  if (!status) return "Open";

  switch (status.toLowerCase()) {
    case "open":
      return "Open";
    case "in progress":
      return "In Progress";
    case "waiting for donors confirmation":
      return "Waiting for Donors Confirmation";
    case "closed":
      return "Closed";
    default:
      return "Open";
  }
};